import { useState, useEffect } from 'react';
import { API_BASE } from './config/api';
import { BrowserRouter, Routes, Route, NavLink, Outlet } from 'react-router-dom';
import { Home, Calendar, FileText, User, CreditCard } from 'lucide-react';
import { LoginPage } from './pages/LoginPage';
import { HomePage } from './pages/HomePage';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { RecordsPage } from './pages/RecordsPage';
import { BookAppointmentPage } from './pages/BookAppointmentPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { ConsentPage } from './pages/ConsentPage';
import { TelehealthPage } from './pages/TelehealthPage';
import { I18nProvider, useI18n, LanguageToggle } from './i18n';

function BottomNav() {
    const { t } = useI18n();
    return (
        <nav className="bottom-nav" id="bottom-nav">
            <NavLink to="/" end className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}>
                <Home size={22} />
                <span>Inicio</span>
            </NavLink>
            <NavLink to="/appointments" className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}>
                <Calendar size={22} />
                <span>{t('nav.appointments')}</span>
            </NavLink>
            <NavLink to="/invoices" className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}>
                <CreditCard size={22} />
                <span>Facturas</span>
            </NavLink>
            <NavLink to="/records" className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}>
                <FileText size={22} />
                <span>Historia</span>
            </NavLink>
            <NavLink to="/profile" className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}>
                <User size={22} />
                <span>Perfil</span>
            </NavLink>
        </nav>
    );
}

function AppShell() {
    return (
        <div className="app-shell">
            <Outlet />
            <BottomNav />
        </div>
    );
}

function ProfilePage() {
    const [patient, setPatient] = useState<Record<string, any> | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${API_BASE}/patients?limit=1`)
            .then(r => r.json())
            .then(json => setPatient(json.data?.[0] || null))
            .catch(() => { })
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="fade-in">
            <div className="portal-header">
                <h1>Mi Perfil</h1>
                <p>Información personal</p>
            </div>
            <div className="portal-content">
                {loading ? (
                    <div className="portal-card" style={{ padding: 32 }}>
                        <div className="skeleton" style={{ height: 120 }} />
                    </div>
                ) : patient ? (
                    <>
                        {/* Avatar + Name */}
                        <div className="portal-card" style={{ textAlign: 'center', padding: 24 }}>
                            <div style={{
                                width: 64, height: 64, borderRadius: '50%',
                                background: 'var(--color-primary, #0D7C7C)', color: 'white',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 24, fontWeight: 700, margin: '0 auto 12px',
                            }}>
                                {patient.firstName?.[0]}{patient.lastName?.[0]}
                            </div>
                            <h2 style={{ margin: 0 }}>{patient.firstName} {patient.lastName}</h2>
                            <p style={{ color: 'var(--color-text-secondary, #888)', fontSize: 14, margin: '4px 0' }}>
                                ID: {patient.clinicPatientId}
                            </p>
                        </div>

                        {/* Contact Info */}
                        <div className="portal-card" style={{ padding: 20 }}>
                            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Datos de contacto</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--color-text-secondary, #888)' }}>Teléfono</span>
                                    <span>{patient.mobilePhone || '—'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--color-text-secondary, #888)' }}>Email</span>
                                    <span>{patient.email || '—'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--color-text-secondary, #888)' }}>Cédula</span>
                                    <span>{patient.cedula || '—'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ color: 'var(--color-text-secondary, #888)' }}>Fecha de nacimiento</span>
                                    <span>{patient.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString('es-CO') : '—'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Language Selection */}
                        <div className="portal-card" style={{ padding: 20 }}>
                            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Idioma / Language</h3>
                            <LanguageToggle />
                        </div>

                        {/* Preferences */}
                        <div className="portal-card" style={{ padding: 20 }}>
                            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Preferencias</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>WhatsApp recordatorios</span>
                                    <span style={{ color: patient.whatsappOptIn ? 'var(--color-success, green)' : '#999' }}>
                                        {patient.whatsappOptIn ? '✓ Activado' : 'Desactivado'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Encuestas NPS</span>
                                    <span style={{ color: patient.npsOptIn ? 'var(--color-success, green)' : '#999' }}>
                                        {patient.npsOptIn ? '✓ Activado' : 'Desactivado'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="portal-card" style={{ textAlign: 'center', padding: 32, color: 'var(--color-text-muted)' }}>
                        <User size={48} style={{ opacity: 0.3, marginBottom: 12 }} />
                        <p>No se pudo cargar el perfil</p>
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                            <LanguageToggle />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export function App() {
    // Auto-login in demo/dev mode (skip OTP screen)
    const isDemo = !!import.meta.env.VITE_API_URL || import.meta.env.DEV;
    const [loggedIn, setLoggedIn] = useState(isDemo);

    if (!loggedIn) {
        return (
            <I18nProvider defaultLocale="es">
                <LoginPage onLogin={() => setLoggedIn(true)} />
            </I18nProvider>
        );
    }

    return (
        <I18nProvider defaultLocale="es">
            <BrowserRouter basename="/portal">
                <Routes>
                    <Route element={<AppShell />}>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/appointments" element={<AppointmentsPage />} />
                        <Route path="/book" element={<BookAppointmentPage />} />
                        <Route path="/records" element={<RecordsPage />} />
                        <Route path="/invoices" element={<InvoicesPage />} />
                        <Route path="/consent/:consentId" element={<ConsentPage />} />
                        <Route path="/telehealth/:appointmentId" element={<TelehealthPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="*" element={<HomePage />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </I18nProvider>
    );
}
